import TextField from "@mui/material/TextField";
import FormikInputLabel from "components/disw/FormikInputLabel";
import { useInputLabelStyles } from "components/disw/InputLabelStyle";
import {
  Field,
  FieldProps,
  FormikErrors,
  FormikTouched,
  useFormikContext
} from "formik";
import errorStatusIcon from "./errorStatus.svg";
import useStyles from "./FormikTextField.styles";

export interface FormikTextFieldProps<T> extends Partial<FieldProps> {
  name: keyof T & string;
  label: string;
  type?: "text" | "email" | "password" | "number";
  placeholder?: string;
  required?: boolean;
  helperText?: string;
  hinttext?: string;
  disabled?: boolean;
  readOnly?: boolean;
  errorMessage?: string;
  onChange?: (e: React.ChangeEvent<HTMLInputElement>) => void;
}

export const FormikTextField = <T extends object>({
  name,
  label,
  type = "text",
  placeholder = "",
  required = false,
  helperText,
  readOnly = false,
  error,
  errorMessage,
  onChange,
  ...props
}: FormikTextFieldProps<T> & { error?: boolean }) => {
  const classes = useStyles();
  const labelClasses = useInputLabelStyles();
  let errors: FormikErrors<T> = {},
    touched: FormikTouched<T> = {};
  try {
    const formik = useFormikContext<T>();
    errors = formik.errors;
    touched = formik.touched;
  } catch {}

  const isError =
    typeof error === "boolean" ? error : Boolean(errors[name] && touched[name]);

  const inputProps = {
    classes: { root: classes.input },
    endAdornment: isError ? (
      <span className={classes.errorIconWrapper}>
        <img src={errorStatusIcon} alt="Error" />
      </span>
    ) : undefined,
    autoComplete: type === "email" ? "new-password" : "off",
    ...(readOnly ? { readOnly: true } : {})
  };

  return (
    <div className={classes.root}>
      <FormikInputLabel
        name={name}
        required={required}
        label={label}
        isError={isError}
        errorMessage={errorMessage}
        labelClasses={labelClasses}
        disabled={props.disabled}
      />
      <Field
        as={TextField}
        id={name}
        name={name}
        type={type}
        placeholder={placeholder}
        fullWidth
        helperText={helperText}
        variant="outlined"
        InputLabelProps={{ shrink: false }}
        InputProps={inputProps}
        error={Boolean(isError)}
        aria-invalid={isError}
        aria-describedby={isError ? `${name}-error-text` : undefined}
        onChange={onChange}
        {...props}
      />
      {props.hinttext && (
        <div className={labelClasses.hintText}>{props.hinttext}</div>
      )}
    </div>
  );
};
